import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Organization, Site } from '../../../models/organization.model';
import { loadProductSitePerformances } from '../../../store/actions/pd.actions';
import {
	selectProductSitePerformances,
	selectProductSitePerformancesLoading,
	selectSelectedEvent
} from '../../../store/selectors/pd.selectors';
import { DispatchPortfolioFiltersComponent } from './dispatch-portfolio-filters/dispatch-portfolio-filters.component';
import { RegistrationDetailsComponent } from './registration-details/registration-details.component';

@Component({
  selector: 'fdr-past-performance-ui-dispatch-portfolio',
  imports: [DispatchPortfolioFiltersComponent, RegistrationDetailsComponent, TranslateModule],
  templateUrl: './dispatch-portfolio.component.html',
  styleUrl: './dispatch-portfolio.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DispatchPortfolioComponent {
  @ViewChild('registrationTable') registrationTable!: RegistrationDetailsComponent;

  private readonly store = inject(Store);

  private readonly selectedEvent = this.store.selectSignal(selectSelectedEvent);

  readonly rowsSignal = this.store.selectSignal(selectProductSitePerformances);

  readonly organizationsSignal = computed<Organization[]>(() => {
    const rows = this.rowsSignal();
    const map = new Map<string, Organization>();

    rows.forEach(row => {
      const orgId = row.organizationId || row.organization;
      const orgName = row.organization || orgId;
      if (!orgId || !orgName) return;
      if (!map.has(orgId)) {
        map.set(orgId, { id: orgId, name: orgName });
      }
    });

    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  });

  readonly sitesSignal = computed<Site[]>(() => {
    const rows = this.rowsSignal();
    const siteMap = new Map<string, Site>();

    rows.forEach(row => {
      const siteId = row.site || row.id;
      const siteName = row.site;
      const orgId = row.organizationId || row.organization || '';
      if (!siteId || !siteName) return;
      if (!siteMap.has(siteId)) {
        siteMap.set(siteId, { id: siteId, name: siteName, organizationId: orgId });
      }
    });

    return [...siteMap.values()].sort((a, b) => a.name.localeCompare(b.name));
  });
  readonly isLoading = this.store.selectSignal(selectProductSitePerformancesLoading);

  private readonly appliedFilters = signal<{ organizationNames: string[]; siteNames: string[] }>({ organizationNames: [], siteNames: [] });

  readonly filteredRowsSignal = computed(() => {
    const rows = this.rowsSignal();
    const { organizationNames, siteNames } = this.appliedFilters();

    if (!organizationNames.length && !siteNames.length) return rows;

    return rows.filter(row => {
      const matchesOrg = organizationNames.length === 0 || organizationNames.includes(row.organization);
      const matchesSite = siteNames.length === 0 || siteNames.includes(row.site);
      return matchesOrg && matchesSite;
    });
  });

  constructor() {
    effect(() => {
      const event = this.selectedEvent();
      if (!event?.productId || !event.eventId) return;

      this.store.dispatch(loadProductSitePerformances({ productId: event.productId, eventId: event.eventId }));
    });
  }

  exportCSV() {
    this.registrationTable.exportCSV();
  }

  onAppliedFiltersChange(filters: { organizationNames: string[]; siteNames: string[] }) {
    this.appliedFilters.set(filters);
  }

}
